import type { FormulationEntry } from "@/utils/types/compound";

// Concentration units that convert linearly onto a common ppm scale. Other
// units (mol%, mg, g, …) aren't inter-convertible without MW/density, so an
// addition given in a non-conc unit that differs from the target row's unit
// falls back to raw summation (best-effort).
const CONC_TO_PPM: Record<string, number> = { ppm: 1, ppb: 1e-3, ppt: 1e-6 };

/** Express `addPpm` (given in `addUnit`) on `targetUnit`'s scale. */
function inUnit(
  addPpm: number,
  addUnit: FormulationEntry["amountUnit"],
  targetUnit: FormulationEntry["amountUnit"],
): number {
  if (addUnit === targetUnit) return addPpm;
  const f = addUnit ? CONC_TO_PPM[addUnit] : undefined;
  const t = targetUnit ? CONC_TO_PPM[targetUnit] : undefined;
  return f !== undefined && t !== undefined ? addPpm * (f / t) : addPpm;
}

/**
 * Apply ingredient `additions` to `base`, INCREASING the concentration of any
 * compound already present instead of appending a duplicate row. A genuinely
 * new compound is appended; an existing one has the addition summed into its
 * amount (unit-normalized to the existing row's unit). The flask must hold one
 * row per compound — adding an ingredient that's already there is a dose-up,
 * never a second row.
 *
 * Pure: returns a new array, never mutates the inputs.
 */
export function mergeFormulationAdditions(
  base: readonly FormulationEntry[],
  additions: readonly FormulationEntry[],
): FormulationEntry[] {
  const out = base.map((e) => ({ ...e }));
  const idxById = new Map(out.map((e, i) => [e.compoundId, i] as const));
  for (const add of additions) {
    const idx = idxById.get(add.compoundId);
    if (idx === undefined) {
      idxById.set(add.compoundId, out.length);
      out.push({ ...add });
    } else {
      const cur = out[idx]!;
      out[idx] = {
        ...cur,
        ppm: cur.ppm + inUnit(add.ppm, add.amountUnit, cur.amountUnit),
      };
    }
  }
  return out;
}
